/**
 * Formatting utilities
 */

/**
 * Formats a number as currency with European format
 * @param value - The number to format
 * @param currency - Currency symbol (default: '€')
 * @returns Formatted currency string (e.g., "1.234,56 €")
 */
const groupThousands = (input: string): string => {
    const isNegative = input.startsWith('-');
    const digits = isNegative ? input.slice(1) : input;
    const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return isNegative ? `-${grouped}` : grouped;
};

const sanitizeNumber = (value: number): number =>
    Number.isFinite(value) ? value : 0;

export function formatCurrency(value: number, currency: string = '€'): string {
    const safeValue = sanitizeNumber(value);
    const [integerPart, decimalPart = '00'] = safeValue
        .toFixed(2)
        .split('.');
    const groupedInteger = groupThousands(integerPart);
    return `${groupedInteger},${decimalPart} ${currency}`.trim();
}

/**
 * Formats a number with European format (thousands separator and decimal comma)
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string (e.g., "1.234,56")
 */
export function formatNumber(value: number, decimals: number = 2): string {
    const safeValue = sanitizeNumber(value);
    const normalizedDecimals = Math.max(0, Math.min(6, Math.floor(decimals)));
    const [integerPart, decimalPart = ''] = safeValue
        .toFixed(normalizedDecimals)
        .split('.');
    const groupedInteger = groupThousands(integerPart);
    if (normalizedDecimals === 0) {
        return groupedInteger;
    }
    return `${groupedInteger},${decimalPart}`;
}

/**
 * Formats a number as percentage
 * @param value - The number to format (0-100)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string (e.g., "85,5%")
 */
export function formatPercentage(value: number, decimals: number = 0): string {
    return `${formatNumber(value, decimals)}%`;
}
